import { useState, useEffect, useRef } from 'react';
import { BRAND } from '@/mocks/brandData';

export interface HeroSectionProps {
  headline: string;
  subheadline: string;
  ctaText: string;
  ctaHref: string;
  secondaryCta?: { text: string; href: string };
  backgroundImage: string;
  badge?: string;
}

export default function HeroSection({
  headline,
  subheadline,
  ctaText,
  ctaHref,
  secondaryCta,
  backgroundImage,
  badge,
}: HeroSectionProps) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = new Image();
    img.src = backgroundImage;
    img.onload = () => setLoaded(true);
    if (imgRef.current?.complete) setLoaded(true);
  }, [backgroundImage]);

  const words = headline.split(' ');

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-jc-dark">
      {/* Background image with Ken Burns */}
      <div className={`absolute inset-0 z-0 transition-opacity duration-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
        <img
          ref={imgRef}
          src={backgroundImage}
          alt="JC Cooks culinary experience"
          className="w-full h-full object-cover object-center scale-[1.08] animate-[kenBurns_16s_ease-out_forwards]"
        />
        {/* Cinematic multi-layer overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/15"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/25"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30"></div>
      </div>

      {/* Thin gold line accent */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-jc-gold/40 to-transparent z-10 hidden lg:block"></div>

      {/* Badge */}
      {badge && (
        <div className={`absolute top-28 right-8 md:right-14 z-20 transition-all duration-1000 delay-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="bg-black/30 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3.5 text-center max-w-[140px]">
            <i className="ri-award-fill text-jc-gold text-xl mb-1.5 block"></i>
            <p className="text-white text-[10px] font-semibold tracking-wider uppercase leading-tight">{badge}</p>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-40 md:py-48">
        <div className="max-w-[680px]">
          {/* Eyebrow label */}
          <div className={`flex items-center gap-3 mb-7 transition-all duration-700 delay-100 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
            <span className="w-10 h-px bg-jc-gold"></span>
            <span className="text-jc-gold text-[10px] font-bold tracking-[0.22em] uppercase">{BRAND.name}</span>
          </div>

          {/* Animated headline — word by word */}
          <h1 className="font-serif text-[clamp(2.4rem,5.5vw,4.5rem)] text-white leading-[1.08] font-light mb-7">
            {words.map((word, i) => (
              <span
                key={i}
                className="inline-block mr-[0.25em] last:mr-0"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease ${0.2 + i * 0.06}s, transform 0.6s ease ${0.2 + i * 0.06}s`,
                }}
              >
                {word}
              </span>
            ))}
          </h1>

          {/* Subheadline */}
          <div className={`transition-all duration-700 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <p className="text-white/65 text-[1.05rem] leading-relaxed mb-10 max-w-[520px] font-light">
              {subheadline}
            </p>
          </div>

          {/* CTAs */}
          <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-[600ms] ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <a href={ctaHref}
              className="inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-jc-gold text-jc-charcoal font-bold text-[11px] tracking-[0.18em] uppercase rounded-full hover:bg-white hover:text-jc-charcoal transition-all duration-300 whitespace-nowrap group">
              {ctaText}
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </a>
            {secondaryCta && (
              <a href={secondaryCta.href}
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 border border-white/30 text-white font-semibold text-[11px] tracking-[0.18em] uppercase rounded-full hover:bg-white/10 hover:border-white/60 transition-all duration-300 whitespace-nowrap">
                <i className="ri-phone-line text-jc-gold"></i>
                {secondaryCta.text}
              </a>
            )}
          </div>

          {/* Social proof strip */}
          <div className={`flex items-center gap-5 mt-14 flex-wrap transition-all duration-700 delay-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-jc-gold text-xs"></i>)}
              <span className="text-white/45 text-xs ml-1.5 font-medium">5-Star Rated</span>
            </div>
            <span className="w-px h-4 bg-white/15"></span>
            <span className="text-white/45 text-xs font-medium">20+ Years Experience</span>
            <span className="w-px h-4 bg-white/15"></span>
            <span className="text-white/45 text-xs font-medium">San Antonio, TX</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-white/25 text-[9px] font-semibold tracking-[0.2em] uppercase">Discover</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/30 to-transparent animate-pulse"></div>
      </div>
    </section>
  );
}
